import { Button, Intent } from '@blueprintjs/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICellRendererParams } from '@ag-grid-community/core';
import cn from 'classnames';
import { DeleteIconButton, EditIconButton } from 'es/app/components/buttons/IconButton';
import { TFunction } from 'i18next';
import React, { MouseEvent } from 'react';
import { Translation } from 'react-i18next';
import isNil from 'lodash/fp/isNil';

export interface IActionCellProps<TItem> {
    onDelete?: (item: TItem) => void;
    onEdit?: (item: TItem) => void;

    canEdit?: boolean | ((item: TItem) => boolean);
    canDelete?: boolean | ((item: TItem) => boolean);
    CustomAction?: ({ item }: { item: TItem }) => React.ReactNode;
}

interface IState {
    isWaitingForConfirmation: boolean;
}

class ActionCell<TItem> extends React.PureComponent<
    React.PropsWithChildren<ICellRendererParams & IActionCellProps<TItem>>,
    IState
> {
    private confirmationTimer: number | null = null;

    constructor(props: React.PropsWithChildren<ICellRendererParams & IActionCellProps<TItem>>) {
        super(props);

        this.state = {
            isWaitingForConfirmation: false,
        };
    }

    public render() {
        return <Translation ns="app">{this.renderWithTranslate}</Translation>;
    }

    private evaluateEnabled = (evaluator?: boolean | ((item: TItem) => boolean)) => {
        if (isNil(evaluator)) return true;

        return typeof evaluator === 'boolean' ? evaluator : evaluator(this.props.node.data as TItem);
    };

    private canEdit = () => this.evaluateEnabled(this.props.canEdit);
    private canDelete = () => this.evaluateEnabled(this.props.canDelete);

    private renderWithTranslate = (t: TFunction) => {
        const className = cn('action-cell-buttons', { 'confirm-delete': this.state.isWaitingForConfirmation });

        const showEditButton = !!this.props.onEdit;
        const showDeleteButton = !!this.props.onDelete;
        const showIconConfirm = this.shouldUseIconConfirmButton();
        const { CustomAction } = this.props;

        return (
            <div className={className}>
                {showEditButton && (
                    <EditIconButton
                        title={t('button.edit', { ns: 'app/views/site/processes' })}
                        onClick={this.handleEdit}
                        disabled={!this.canEdit()}
                    />
                )}
                {CustomAction && <CustomAction item={this.props.node.data} />}

                {this.props.children}

                {showDeleteButton && (
                    <>
                        <DeleteIconButton
                            title={t('button.delete', { ns: 'app/views/site/processes' })}
                            onClick={this.handleDeleteRequested}
                            disabled={!this.canDelete()}
                        />

                        <Button
                            type="button"
                            className="confirmation"
                            onClick={this.handleDeleteConfirmed}
                            intent={Intent.DANGER}
                            onMouseOver={this.handleMouseEnterConfirmButton}
                            onMouseOut={this.handleMouseLeaveConfirmButton}
                        >
                            <>
                                {!showIconConfirm && t('action.deleteConfirm')}
                                {showIconConfirm && <FontAwesomeIcon icon={['fal', 'trash']} />}
                            </>
                        </Button>
                    </>
                )}
            </div>
        );
    };

    public componentWillUnmount = () => this.clearConfirmationTimer();

    private shouldUseIconConfirmButton = () => {
        const cell = this.props.eGridCell;
        if (!cell) return false;

        return cell.getBoundingClientRect().width <= 80;
    };

    private clearConfirmationTimer = () => {
        if (!this.confirmationTimer) return;

        clearTimeout(this.confirmationTimer);
        this.confirmationTimer = null;
    };

    private handleEdit = (event: MouseEvent<HTMLButtonElement>) => {
        if (!this.canEdit()) return;

        event.stopPropagation();
        this.props.onEdit?.(this.props.node.data as TItem);
    };

    private handleDeleteRequested = (event: MouseEvent<HTMLButtonElement>) => {
        if (!this.canDelete()) return;

        event.stopPropagation();
        event.preventDefault();

        const deleteButton = event.target;
        setTimeout(() => 'blur' in deleteButton && typeof deleteButton.blur === 'function' && deleteButton.blur());

        this.setState({ isWaitingForConfirmation: true });
    };

    private handleDeleteConfirmed = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        this.setState({ isWaitingForConfirmation: false });
        this.props.onDelete?.(this.props.node.data as TItem);
    };

    private handleMouseEnterConfirmButton = () => {
        this.clearConfirmationTimer();
    };

    private handleMouseLeaveConfirmButton = () => {
        this.confirmationTimer = window.setTimeout(() => {
            this.setState({ isWaitingForConfirmation: false });
            this.confirmationTimer = null;
        }, 3000);
    };
}

export default ActionCell;
